'use client';

import React, { useMemo } from 'react';
import {
  faUpRightAndDownLeftFromCenter,
  faUserDoctor,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  useGetVolumeByLocationQuery,
  useGetProviderStrategyTabsQuery,
} from 'src/api/redux';
import ScheduleDayCircles from './ScheduleDayCircles';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import StrategyDots from './StrategyDots';

interface TopProviderCardProps {
  provider: Record<string, any>;
  siteId: string;
  codeSetLabel: string;
  queryParams: string;
}

const TopProviderCard: React.FC<TopProviderCardProps> = ({
  provider,
  siteId,
  codeSetLabel,
  queryParams,
}) => {
  const { data: vblData } = useGetVolumeByLocationQuery(
    { providerId: provider.provider_id, queryParams },
    { skip: !provider.provider_id }
  );

  const { hasPermission } = useAuthorization();
  const { data: strategyData } = useGetProviderStrategyTabsQuery(
    { providerId: provider.provider_id },
    {
      skip: !provider.provider_id || !hasPermission(TARGET_STRATEGIES_READ),
    }
  );

  const schedule = useMemo(() => {
    if (!vblData?.results) return null;
    const siteMatch = vblData.results.find(
      (loc: Record<string, any>) => loc.provider_id === siteId
    );
    return siteMatch?.schedule ?? null;
  }, [vblData, siteId]);

  const strategyCount = (strategyData as any)?.count ?? 0;

  const claimsDisplay = provider.claims
    ? provider.claims === '<11'
      ? provider.claims
      : Number(provider.claims).toLocaleString()
    : '0';

  return (
    <div
      className="rounded-2xl bg-white shadow-sm flex flex-col h-full hover:shadow-lg hover:scale-[1.02] transition-all"
      data-testid={`top-provider-card-${provider.provider_id}`}
      role="region"
      aria-label={provider.name}
    >
      <div className="px-4 pt-4 pb-3 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-sm font-bold text-neutral-800 leading-snug"
            data-testid={`top-provider-name-${provider.provider_id}`}
          >
            {provider.name || 'N/A'}
          </p>
          <span className="shrink-0 flex items-center justify-center w-6 h-6">
            <Icon
              icon={faUpRightAndDownLeftFromCenter}
              className="w-3 h-3 text-neutral-700"
            />
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          {provider.specialty && (
            <span className="flex items-center gap-1">
              <Icon icon={faUserDoctor} className="w-3 h-3" />
              {provider.specialty}
            </span>
          )}
          <StrategyDots
            count={strategyCount}
            data-testid={`top-provider-strategies-${provider.provider_id}`}
          />
        </div>
      </div>

      <div className="border-t border-neutral-300" />

      <div className="px-4 pt-3 pb-4 flex flex-col gap-2">
        <div>
          <p className="text-sm font-bold text-neutral-800">
            {codeSetLabel}
          </p>
          <p className="text-sm text-neutral-600">
            <span className="text-lg font-semibold text-sky-600">{claimsDisplay}</span> at this location
          </p>
        </div>
        {schedule && (
          <ScheduleDayCircles
            schedule={schedule}
            data-testid={`top-provider-schedule-${provider.provider_id}`}
          />
        )}
      </div>
    </div>
  );
};

export default TopProviderCard;
